import * as R from 'ramda';
import * as yup from 'yup';
import i18n from 'helpers/i18n';
import messages from './messages';
import { useState } from 'react';
import { Box } from 'components/Box';
import { Wrapper, Title } from './Styled';
import { MetaDataOption, MetaDataOptionProps } from './MetaDataOption';
import { CycleMetaDataValue } from 'types/api/envScales/CycleMetaDataValue';
import { MetaDataCategory } from 'types/api/envScales/MetaDataCategory';
import { ObservationCycle } from 'types/api/envScales/ObservationCycle';
import { updateCycleMetaData } from 'actions/envScales/observationCycles';
import { getInitialValues, getPayloadValues } from './utils';
import { ErrorMessage } from 'components/Measure/View/Styled';
import useDebouncedEffect from 'hooks/useDebouncedEffect';
import { useServerFieldError } from 'components/Measure/View/ValidationsContext';

interface MetaDataMultiSelectProps {
  /** Observation ID. */
  observationId: number;

  /** Cycle */
  cycle: ObservationCycle;

  /** Initial meta data values. */
  values: CycleMetaDataValue[];

  /** Meta data category. */
  category: MetaDataCategory;
}

function MetaDataMultiSelect({
  observationId,
  cycle,
  category,
  values: initialValues
}: MetaDataMultiSelectProps) {
  const [touched, setTouched] = useState(false);
  const [values, setValues] = useState(
    getInitialValues(category, initialValues)
  );

  const checkedChoiceNames = Object.keys(values).filter(
    name => values[name].isChecked
  );
  const primaryChoiceNames = Object.keys(values).filter(
    name => values[name].isPrimary
  );

  const categoryError = useServerFieldError(
    `cycles.${cycle.cycle_number}.details.${category.t_name}.error`,
    checkedChoiceNames,
    yup.array().min(1)
  );
  const categoryPrimaryError = useServerFieldError(
    `cycles.${cycle.cycle_number}.details.${category.t_name}.primary_error`,
    primaryChoiceNames,
    yup.array().min(1)
  );

  useDebouncedEffect(
    () => {
      if (!touched) {
        return;
      }

      const payload = {
        meta_data: {
          meta_data_category_id: category.id,
          values: getPayloadValues(values)
        }
      };

      updateCycleMetaData(observationId, cycle.id, payload);
    },
    1000,
    [values, touched]
  );

  const handleChange: MetaDataOptionProps['onChange'] = data => {
    let newValues = { ...values };
    const isNewPrimary = data.value.isPrimary;

    if (isNewPrimary) {
      newValues = R.mapObjIndexed(
        value => ({ ...value, isPrimary: false }),
        newValues
      );
    }

    const mergedValues = { ...newValues, [data.name]: data.value };
    setValues(mergedValues);
    setTouched(true);
  };

  const categoryErrorMessage = [
    categoryError?.message,
    categoryPrimaryError?.message
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Wrapper>
      <Title>{i18n.ft(messages.type, { category: category.name })}</Title>
      <Box mt="12px">{category.description}</Box>

      {categoryErrorMessage && (
        <ErrorMessage>{categoryErrorMessage}</ErrorMessage>
      )}

      <Box mt="32px">
        {category.choices.map(choice => (
          <MetaDataOption
            key={choice.id}
            choice={choice}
            value={values[choice.t_name]}
            onChange={handleChange}
          />
        ))}
      </Box>
    </Wrapper>
  );
}

export default MetaDataMultiSelect;
